<template>
  <div class="bank-id-sweden-container">
    <div v-if="showStatus" class="status-container">
      <EidStatus :title="userMessage.title" :description="userMessage.description">
        <template v-if="state !== 'cancelling'" #button>
          <FriedLink @click="handleCancel">
            {{ t('cancel') }}
          </FriedLink>
        </template>
      </EidStatus>
    </div>
    <div v-else class="start-container">
      <FriedMessage
        v-if="failedReason"
        class="message"
        :type="MessageType.Error"
        :title="failedReason.title"
      >
        {{ failedReason.description }}
      </FriedMessage>
      <div class="sections">
        <a data-external="bank-id-sweden-open-app-button" :href="appUrl" referrerPolicy="origin">
          {{ t('cr_open_bankid_on_this_device') }}
        </a>
        <OrDivider class="divider" />
        <div class="qr-code-section">
          <FriedH4>{{ t('cr_scan_qr_code') }}</FriedH4>
          <div class="qr-code">
            <div class="qr-code-image-container">
              <FriedSkeleton v-if="!qrCodeUrl" class="qr-code-image" />
              <img
                v-else
                alt="qr-code"
                :src="qrCodeUrl"
                class="qr-code-image"
                data-external="bank-id-sweden-qr-code-image"
              />
            </div>
            <div class="instructions-container">
              <ol data-external="bank-id-sweden-instructions-container">
                <li>{{ $t('cr_bankid_instructions_bullet_one') }}</li>
                <li>{{ $t('cr_bankid_instructions_bullet_two') }}</li>
                <li>{{ $t('cr_bankid_instructions_bullet_three') }}</li>
                <li>{{ $t('cr_bankid_instructions_bullet_four') }}</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { Ref } from 'vue';
import { computed, watch, ref } from 'vue';
import QRCode from 'qrcode';
import { computedAsync } from '@vueuse/core';
import { MessageType } from '@getaccept/fried-tofu';
import { SigningHelper } from '../../helpers/signing.helper';
import { useBankIdSweden } from '../composables/bank-id-sweden.composable';
import { useRootStore } from '../../../store/root.store';
import { BankIdSwedenHelper } from '../helpers/bank-id-sweden.helper';
import type { UserMessage } from '../types/bank-id-sweden';
import { useDocumentStore } from '../../../documents/store/document.store';
import { useResumeEidSigning } from '../composables/resume-eid-signing.composable';
import OrDivider from './OrDivider.vue';
import EidStatus from './EidStatus.vue';

const emit = defineEmits(['sign']);

const { t } = useI18n();
const rootStore = useRootStore();
const documentStore = useDocumentStore();
const { bankIdSwedenOrderRef, bankIdSwedenAutoStartToken } = useResumeEidSigning();

const signingAttempted = ref(false);
const failedReason: Ref<UserMessage> = ref(null);

const { state, statusCode, qrCode, appUrl, start, cancel, resume } = useBankIdSweden({
  documentId: documentStore.document.id,
  recipientId: rootStore.recipientId,
});

const userMessage = computed(() =>
  BankIdSwedenHelper.getUserMessage(state.value, statusCode.value)
);

const showStatus = computed(() => ['signing', 'cancelling'].includes(state.value));

const isDocumentSigned = async () => {
  await documentStore.loadRecipients();
  return SigningHelper.isSignedByRecipient(
    documentStore.document.recipients,
    rootStore.recipientId
  );
};

const handleFailed = async () => {
  if (signingAttempted.value) {
    const isSigned = await isDocumentSigned();
    if (isSigned) {
      emit('sign');
      return;
    }
  }

  failedReason.value = userMessage.value;
  start();
};

watch(
  state,
  () => {
    switch (state.value) {
      case 'not_started':
        signingAttempted.value = false;
        break;
      case 'signing':
        failedReason.value = null;
        signingAttempted.value = true;
        break;
      case 'failed':
        handleFailed();
        break;
      case 'completed':
        emit('sign');
        break;
    }
  },
  { immediate: true }
);

const handleCancel = () => {
  cancel();
  start();
};

const init = () => {
  if (bankIdSwedenOrderRef.value) {
    resume(bankIdSwedenOrderRef.value, bankIdSwedenAutoStartToken.value);
  } else {
    start();
  }
};

const qrCodeUrl = computedAsync<string>(async () => {
  try {
    return await QRCode.toDataURL(qrCode.value, { margin: 0 });
  } catch (error) {
    return '';
  }
}, null);

init();
</script>
<style lang="scss" scoped>
$qr-size: 120px;

.bank-id-sweden-container {
  .status-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    min-height: 238px;
  }

  .start-container {
    .message {
      margin-top: var(--spacing-50);
      margin-bottom: var(--spacing-150);
    }

    .sections {
      display: flex;
      flex-direction: column-reverse;
      min-height: 238px;
      justify-content: flex-start;

      @media screen and (max-width: $xs) {
        flex-direction: column;
        height: 100%;
      }

      .divider {
        padding: var(--spacing-150) 0;
      }

      .qr-code-section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-150);

        .qr-code {
          display: flex;
          gap: 3rem;
          @media screen and (max-width: $xs) {
            flex-direction: column;
            gap: var(--spacing-150);
          }

          .qr-code-image-container {
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            height: 100%;

            .qr-code-image {
              width: $qr-size;
              height: $qr-size;
            }
          }

          .instructions-container {
            align-self: flex-start;
            width: 100%;

            ol {
              margin-block-end: 0;
              margin-block-start: 0;
              padding-inline-start: 0;
              list-style-position: inside;

              li {
                padding-bottom: var(--spacing-25);
              }
            }
          }
        }
      }
    }
  }
}
</style>
